/**
 * Forecast totals for chart footers (Fan, 2026-08-31: "On all dashboards and
 * pages with forecasts, please give the total for them (FR + UK together)").
 *
 * Every chart already carries a YTD footer with a Combined figure; the
 * forecast months (the dashed tail) had per-series values only and no total.
 * This module sums the forecast window from the SAME chart data array the
 * chart renders, so the footer can never disagree with the bars/lines above
 * it -- no second fetch, no re-derivation from the API.
 *
 * Two data shapes exist in the app and both are handled:
 *  - merged (mergeWithForecast): forecast values overwrite the series key on
 *    rows tagged `_isForecast: true`.
 *  - split (splitForecastSeries): forecast values live on `<key>_forecast`
 *    and the series key is nulled on those rows. The boundary "anchor" row
 *    (last actuals month) also carries a `<key>_forecast` copy for visual
 *    continuity -- it is an ACTUAL and must never be counted, which is why
 *    rows are selected on `_isForecast`, never on the presence of a
 *    `_forecast` value.
 *
 * Values are read in the chart's own display currency, so summing FR + UK is
 * legitimate: the chart has already converted both into one currency. If a
 * caller ever passes a mixed-currency array, that is a bug in the caller, not
 * something this helper can detect.
 */

function usesSplitKeys(data, key) {
  return (data || []).some(function (row) {
    return row != null && row[key + '_forecast'] != null;
  });
}

/**
 * Read one series' forecast contribution from a row.
 *
 * When the array carries `<key>_forecast` fields for this series (the
 * splitForecastSeries / group-chart shape), ONLY that field counts: on a
 * current-month partial the plain key holds the actuals-to-date portion, and
 * in forecast months the plain key is a real 0. When the array has no such
 * field for the series (the mergeWithForecast shape), the plain key on a row
 * tagged `_isForecast` is the forecast value.
 */
function forecastValue(row, key, split) {
  if (row == null) return null;
  var v = split ? row[key + '_forecast'] : row[key];
  return v == null || isNaN(v) ? null : v;
}

/**
 * @param {Array} data - chart data rows (as rendered)
 * @param {string[]} keys - series data keys to total
 * @returns {{ byKey: Record<string, number>, total: number, months: string[], hasForecast: boolean }}
 */
export function computeForecastTotals(data, keys, opts) {
  // includePartial=false: the page's YTD already counts the whole current
  // month (Property Portfolio basis), so its dashed remainder must not be
  // counted a second time in the forecast total.
  var includePartial = !(opts && opts.includePartial === false);
  var byKey = {};
  var months = [];
  var total = 0;
  var seen = false;

  var split = {};
  (keys || []).forEach(function (k) { byKey[k] = 0; split[k] = usesSplitKeys(data, k); });

  (data || []).forEach(function (row) {
    if (!row) return;
    var partial = includePartial && row._isForecast !== true && row._partialForecast === true;
    if (row._isForecast !== true && !partial) return;
    var rowCounted = false;
    (keys || []).forEach(function (k) {
      // Current month on a merged-shape chart: only its still-to-come part
      // (markCurrentMonthPartial) belongs to the forecast window.
      var v = partial ? row['_fwd_' + k] : forecastValue(row, k, split[k]);
      if (v == null || isNaN(v)) return;
      byKey[k] += v;
      total += v;
      rowCounted = true;
    });
    if (rowCounted) {
      seen = true;
      if (row.month && months.indexOf(row.month) === -1) months.push(row.month);
    }
  });

  return { byKey: byKey, total: total, months: months, hasForecast: seen };
}

/**
 * Average (not sum) of a forecast series -- for rate/percentage charts where
 * adding months together is meaningless (occupancy, ADR, RevPAR).
 */
export function computeForecastAverages(data, keys) {
  var sums = {};
  var counts = {};
  var months = [];
  var seen = false;

  var split = {};
  (keys || []).forEach(function (k) { sums[k] = 0; counts[k] = 0; split[k] = usesSplitKeys(data, k); });

  (data || []).forEach(function (row) {
    if (!row || row._isForecast !== true) return;
    var rowCounted = false;
    (keys || []).forEach(function (k) {
      var v = forecastValue(row, k, split[k]);
      if (v == null || isNaN(v)) return;
      sums[k] += v;
      counts[k] += 1;
      rowCounted = true;
    });
    if (rowCounted) {
      seen = true;
      if (row.month && months.indexOf(row.month) === -1) months.push(row.month);
    }
  });

  var byKey = {};
  (keys || []).forEach(function (k) {
    byKey[k] = counts[k] > 0 ? sums[k] / counts[k] : null;
  });

  return { byKey: byKey, months: months, hasForecast: seen };
}

/** 'Sep–Nov' (or 'Sep' when the window is a single month), '' when empty. */
export function forecastWindowLabel(months) {
  if (!months || months.length === 0) return '';
  if (months.length === 1) return months[0];
  return months[0] + '\u2013' + months[months.length - 1];
}
